"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InsightList = exports.getInsightListSourceItem = void 0;
var tslib_1 = require("tslib");
// (C) 2022-2023 GoodData Corporation
var react_1 = tslib_1.__importStar(require("react"));
var react_intl_1 = require("react-intl");
var sdk_model_1 = require("@gooddata/sdk-model");
var debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
var range_1 = tslib_1.__importDefault(require("lodash/range"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_kit_1 = require("@gooddata/sdk-ui-kit");
var InsightListNoData_1 = require("./InsightListNoData");
var model_1 = require("../../model");
var locales_1 = require("../../locales");
var ITEMS_PER_PAGE = 50;
var ITEM_HEIGHT = 40;
var LIST_WIDTH = 229;
function getInsightListSourceItem(insight) {
    var insightUrl = sdk_model_1.insightVisualizationUrl(insight);
    var insightType = insightUrl === null || insightUrl === void 0 ? void 0 : insightUrl.split(":")[1];
    return {
        insight: insight,
        insightType: insightType,
    };
}
exports.getInsightListSourceItem = getInsightListSourceItem;
var dropdownTabsTranslationIds = [locales_1.messages.tabsMy, locales_1.messages.tabsAll];
/**
 * @internal
 */
exports.InsightList = function (_a) {
    var height = _a.height, _b = _a.width, width = _b === void 0 ? LIST_WIDTH : _b, searchAutofocus = _a.searchAutofocus, renderItem = _a.renderItem, selectedRef = _a.selectedRef, onSelect = _a.onSelect;
    var intl = react_intl_1.useIntl();
    var backend = sdk_ui_1.useBackendStrict();
    var workspaceId = sdk_ui_1.useWorkspaceStrict();
    var _c = tslib_1.__read(react_1.useState(false), 2), initialLoadCompleted = _c[0], setInitialLoadCompleted = _c[1];
    var _d = tslib_1.__read(react_1.useState([0]), 2), pagesToLoad = _d[0], setPagesToLoad = _d[1]; // first page loaded
    var _e = tslib_1.__read(react_1.useState(""), 2), search = _e[0], setSearch = _e[1];
    var _f = tslib_1.__read(react_1.useState(locales_1.messages.tabsMy.id), 2), selectedTabId = _f[0], setSelectedTabId = _f[1];
    var userRef = model_1.useDashboardSelector(model_1.selectCurrentUserRef);
    var userUri = sdk_model_1.isUriRef(userRef) ? userRef.uri : undefined;
    var insightListLastUpdateRequested = model_1.useDashboardSelector(model_1.selectInsightListLastUpdateRequested);
    var canCreateVisualization = model_1.useDashboardSelector(model_1.selectCanCreateVisualization);
    var allowCreateInsightRequest = model_1.useDashboardSelector(model_1.selectAllowCreateInsightRequest);
    var settings = model_1.useDashboardSelector(model_1.selectSettings);
    var params = pagesToLoad.map(function (pageNumber) { return ({
        limit: ITEMS_PER_PAGE,
        offset: pageNumber * ITEMS_PER_PAGE,
        title: search,
        author: selectedTabId === locales_1.messages.tabsMy.id && !search ? userUri : undefined,
    }); });
    var _g = sdk_ui_1.usePagedResource(function (_a) {
        var limit = _a.limit, offset = _a.offset, title = _a.title, author = _a.author;
        var options = {
            limit: limit,
            offset: offset,
            author: author,
            title: title,
            orderBy: "updated",
        };
        return backend.workspace(workspaceId).insights().getInsights(options);
    }, params, [backend, pagesToLoad, search, selectedTabId, insightListLastUpdateRequested], [search, selectedTabId, pagesToLoad.length === 0, insightListLastUpdateRequested]), insights = _g.items, totalInsightsCount = _g.totalItemsCount, isLoading = _g.isLoading;
    react_1.useEffect(function () {
        if (!initialLoadCompleted && typeof totalInsightsCount !== "undefined") {
            setInitialLoadCompleted(true);
            if (totalInsightsCount === 0) {
                // when the user has no insights of their own, switch to the All tab
                setSelectedTabId(locales_1.messages.tabsAll.id);
            }
        }
    }, [initialLoadCompleted, totalInsightsCount]);
    var onSearch = react_1.useCallback(debounce_1.default(function (searchString) {
        setPagesToLoad([0]);
        setSearch(searchString);
    }, 250), []);
    var itemHeightGetter = function (index) {
        // Modify item heights for first/last item so that their hover states don't overlap.
        // Also @see styles and keep this value in sync with what's in css styles
        // for is-first and is-last in visualization items in the list
        var firstLastItemMargin = 10;
        var isFirstOrLast = totalInsightsCount ? index === 0 || index === totalInsightsCount - 1 : false;
        return isFirstOrLast ? ITEM_HEIGHT + firstLastItemMargin : ITEM_HEIGHT;
    };
    var eventDispatch = model_1.useDashboardEventDispatch();
    var createInsightRequestedEvent = react_1.useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        eventDispatch(model_1.createInsightRequested());
    }, []);
    var TABS_AND_SEARCHFIELD_HEIGHT = 70;
    var SEARCHFIELD_HEIGHT = 47;
    // need to subtract height of controls from the overall size which was measured
    var controlsHeight = search ? SEARCHFIELD_HEIGHT : TABS_AND_SEARCHFIELD_HEIGHT;
    var dropdownListHeight = height && height - controlsHeight;
    var dropdownListLoading = isLoading && insights.length === 0;
    var showDropdownListTabs = initialLoadCompleted && !search;
    var showNoDataCreateButton = allowCreateInsightRequest && canCreateVisualization;
    return (react_1.default.createElement(sdk_ui_kit_1.DropdownList, { width: width, height: dropdownListHeight, isMobile: false, isLoading: dropdownListLoading, showSearch: initialLoadCompleted, searchString: search, searchFieldSize: "small", searchPlaceholder: intl.formatMessage({ id: "search_insights" }), onSearch: onSearch, disableAutofocus: !searchAutofocus, showTabs: showDropdownListTabs, tabs: backend.capabilities.supportsOwners && userUri ? dropdownTabsTranslationIds : undefined, selectedTabId: selectedTabId, onTabSelect: function (_a) {
            var id = _a.id;
            setPagesToLoad([0]);
            setSelectedTabId(id);
        }, itemHeight: ITEM_HEIGHT, itemHeightGetter: itemHeightGetter, items: insights, itemsCount: totalInsightsCount, renderItem: renderItem !== null && renderItem !== void 0 ? renderItem : (function (_a) {
            var _b;
            var insight = _a.item, width = _a.width;
            if (!insight) {
                return react_1.default.createElement(sdk_ui_kit_1.InsightListItem, { isLoading: true });
            }
            var title = sdk_model_1.insightTitle(insight);
            var description = (_b = sdk_model_1.insightSummary(insight)) === null || _b === void 0 ? void 0 : _b.trim();
            var insightListSourceItem = getInsightListSourceItem(insight);
            var isSelected = sdk_model_1.areObjRefsEqual(insight.insight.ref, selectedRef);
            return (react_1.default.createElement(sdk_ui_kit_1.InsightListItem, { title: title, description: description, showDescriptionPanel: settings === null || settings === void 0 ? void 0 : settings.enableDescriptions, type: insightListSourceItem.insightType, width: width, isSelected: isSelected, updated: sdk_model_1.insightUpdated(insightListSourceItem.insight), isLocked: sdk_model_1.insightIsLocked(insightListSourceItem.insight), onClick: function () { return onSelect === null || onSelect === void 0 ? void 0 : onSelect(insight); } }));
        }), renderNoData: function (_a) {
            var hasNoMatchingData = _a.hasNoMatchingData;
            return (react_1.default.createElement(InsightListNoData_1.InsightListNoData, { isUserInsights: selectedTabId === locales_1.messages.tabsMy.id, hasNoMatchingData: hasNoMatchingData, showNoDataCreateButton: showNoDataCreateButton, onCreateButtonClick: createInsightRequestedEvent }));
        }, onScrollEnd: function (startIndex, endIndex) {
            var startPage = Math.floor(startIndex / ITEMS_PER_PAGE);
            var endPage = Math.floor(endIndex / ITEMS_PER_PAGE);
            var pagesToLoad = range_1.default(startPage, endPage + 1);
            setPagesToLoad(pagesToLoad);
        } }));
};
//# sourceMappingURL=InsightList.js.map